import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';

/**
 * A person's own choice about how a screen looks, kept with them on the
 * server (decision 203) — grid or list, the grid's fields, starred reports.
 * It follows them to any register and to a phone; the computer keeps nothing.
 *
 * Changes on screen at once and is saved behind it. If saving fails the
 * screen still shows the choice for now; the next sign-in shows what was
 * saved. While the choices are loading, the screen shows its default.
 *
 * Cached under the person's id: a register is shared, and the next person to
 * tap in on the PIN pad must see their own choices, not the last person's.
 */

type Preferences = Awaited<ReturnType<typeof api.preferences.get.query>>;
export type PreferenceKey = keyof Preferences;

function useEmployeeId(): string | null {
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.auth.me.query() });
  return me.data?.signedIn === true ? me.data.employee.id : null;
}

export function usePreferences(): Preferences | undefined {
  const employeeId = useEmployeeId();
  return useQuery({
    queryKey: ['preferences', employeeId],
    queryFn: () => api.preferences.get.query(),
    enabled: employeeId !== null,
    staleTime: 5 * 60_000,
  }).data;
}

export function usePreference<K extends PreferenceKey>(
  key: K,
  fallback: NonNullable<Preferences[K]>,
): readonly [NonNullable<Preferences[K]>, (next: NonNullable<Preferences[K]> | null) => void] {
  const queryClient = useQueryClient();
  const employeeId = useEmployeeId();
  const preferences = usePreferences();
  const queryKey = ['preferences', employeeId];
  const save = useMutation({
    mutationFn: (next: NonNullable<Preferences[K]> | null) =>
      api.preferences.set.mutate({ key, value: next }),
    onSuccess: (saved) => queryClient.setQueryData(queryKey, saved),
  });
  const value = (preferences?.[key] ?? fallback) as NonNullable<Preferences[K]>;
  const choose = (next: NonNullable<Preferences[K]> | null) => {
    queryClient.setQueryData<Preferences>(queryKey, (current) => {
      const copy: Preferences = { ...(current ?? {}) };
      if (next === null) delete copy[key];
      else copy[key] = next;
      return copy;
    });
    save.mutate(next);
  };
  return [value, choose] as const;
}
